// Store the last cart change event
let lastCartChangeEvent = null;

// Listen for cart changes at the document level
document.addEventListener("cart:change", (event) => {
  // Only store if it's not from our own update
  if (!event.detail?.isOurUpdate) {
    lastCartChangeEvent = event;
  }

  // // Send analytics event for all cart changes
  if (window.Shopify?.analytics?.publish) {
    Shopify.analytics.publish("cart_update", {
      cart: event.detail.cart,
    });
  }
});

class FreeGiftsComponent extends HTMLElement {
  constructor() {
    super();
    this.thresholds = {};
    this.products = {};
    this.addedGifts = new Set();
    this.isOurUpdate = false;

    this.handleCartChange = this.handleCartChange.bind(this);
    this.initializeProperties();
  }

  connectedCallback() {
    if (lastCartChangeEvent) {
      const eventToProcess = lastCartChangeEvent;
      lastCartChangeEvent = null;
      this.handleCartChange(eventToProcess);
    }
  }

  initializeProperties() {
    const config = JSON.parse(this.dataset.config || "{}");
    this.thresholds = config.thresholds || {};
    this.products = config.products || {};
  }

  async handleCartChange(event) {
    if (this.isOurUpdate) {
      return;
    }

    const cart = event.detail.cart;

    this.updateAddedGiftsFromCart(cart);
    await this.handleCartUpdate(cart);
  }

  updateAddedGiftsFromCart(cart) {
    this.addedGifts.clear();

    // Get all valid product IDs (non-empty ones)
    const validProductIds = [this.products.tier1, this.products.tier2, this.products.tier3].filter(
      (id) => id && id !== ""
    );

    cart.items.forEach((item) => {
      const variantId = item.variant_id.toString();
      if (validProductIds.includes(variantId) && item.quantity > 0) {
        this.addedGifts.add(variantId);
      }
    });
  }

  async handleCartUpdate(cart) {
    const shouldHaveGifts = new Set();

    // Only process tiers that have both threshold and product
    if (this.thresholds.tier1 && this.products.tier1) {
      const tier1Threshold = parseFloat(this.thresholds.tier1);
      if (cart.total_price >= tier1Threshold) {
        shouldHaveGifts.add(this.products.tier1);
      }
    }

    if (this.thresholds.tier2 && this.products.tier2) {
      const tier2Threshold = parseFloat(this.thresholds.tier2);
      if (cart.total_price >= tier2Threshold) {
        shouldHaveGifts.add(this.products.tier2);
      }
    }

    if (this.thresholds.tier3 && this.products.tier3) {
      const tier3Threshold = parseFloat(this.thresholds.tier3);
      if (cart.total_price >= tier3Threshold) {
        shouldHaveGifts.add(this.products.tier3);
      }
    }

    // Calculate which gifts need to be added or removed
    const giftsToAdd = [...shouldHaveGifts].filter((id) => !this.addedGifts.has(id));
    const giftsToRemove = [...this.addedGifts].filter((id) => {
      const cartItem = cart.items.find((item) => item.id === parseInt(id));
      if (cartItem) {
        return !shouldHaveGifts.has(id) || cartItem.discounts.length == 0;
      }
      return !shouldHaveGifts.has(id);
    });

    if (giftsToAdd.length === 0 && giftsToRemove.length === 0) {
      return;
    }

    const updates = {};

    for (const giftId of giftsToRemove) {
      updates[giftId] = 0;
      this.addedGifts.delete(giftId);
    }

    for (const giftId of giftsToAdd) {
      updates[giftId] = 1;
      this.addedGifts.add(giftId);
    }

    if (Object.keys(updates).length > 0) {
      try {
        this.isOurUpdate = true;
        const response = await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ updates }),
        });

        // Wait for and parse the updated cart data
        const updatedCart = await response.json();
        console.log("updatedCart", updatedCart);

        // Publish analytics event with updated cart data
        if (window.Shopify?.analytics?.publish) {
          Shopify.analytics.publish("cart_update", {
            cart: updatedCart,
            addedGifts: Array.from(giftsToAdd),
            removedGifts: Array.from(giftsToRemove),
          });
        }

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: { isOurUpdate: true },
          })
        );
      } catch (error) {
        console.error("Error updating gifts:", error);
      } finally {
        this.isOurUpdate = false;
      }
    }
  }
}

if (!customElements.get("free-gifts-component")) {
  customElements.define("free-gifts-component", FreeGiftsComponent);
}
